import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import { Capacitor, registerPlugin } from "@capacitor/core";
import {
  onValue,
  ref,
  remove,
  serverTimestamp,
  set,
  update,
} from "firebase/database";
import { FiX } from "react-icons/fi";
import { getFirebaseClient } from "./firebaseClient";
import "./account.css";

const AuthContext = createContext(null);
const NativeFirebaseAuthentication = registerPlugin("FirebaseAuthentication");

const isNativeApp = () => Capacitor.isNativePlatform();

const publicHandle = (user) => {
  const base = (user.displayName || "viewer").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 16) || "viewer";
  return `${base}-${user.uid.slice(0, 5).toLowerCase()}`;
};

const cleanLibraryItem = (item, mediaType) => ({
  id: Number(item.id),
  mediaType,
  title: item.title || item.name || "Untitled",
  posterPath: item.poster_path || item.posterPath || null,
  backdropPath: item.backdrop_path || item.backdropPath || null,
  overview: (item.overview || "").slice(0, 500),
  releaseDate: item.release_date || item.first_air_date || item.releaseDate || "",
  voteAverage: Number(item.vote_average || item.voteAverage || 0),
  genreIds: (item.genre_ids || item.genres?.map((genre) => genre.id) || item.genreIds || []).slice(0, 8),
  genreNames: (item.genres?.map((genre) => genre.name) || item.genreNames || []).slice(0, 8),
});

const readableAuthError = (error) => {
  if (error?.code === "auth/operation-not-allowed") {
    return "Google sign-in needs to be enabled in Firebase Authentication.";
  }
  if (error?.code === "auth/unauthorized-domain") {
    return "This domain is not authorized for Google sign-in yet.";
  }
  if (error?.code === "auth/popup-closed-by-user") return "Sign-in was cancelled.";
  if (
    error?.code === "12501" ||
    error?.code === "SIGN_IN_CANCELLED" ||
    /cancelled|canceled/i.test(error?.message || "")
  ) {
    return "Sign-in was cancelled.";
  }
  if (/10:|developer_error|configuration/i.test(error?.message || "")) {
    return "Google sign-in is not configured for this APK yet. Check its Firebase SHA fingerprint.";
  }
  return "Google sign-in could not be completed. Please try again.";
};

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [space, setSpace] = useState({ profile: {}, preferences: {}, library: {}, experience: {} });
  const [authOpen, setAuthOpen] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [libraryFeedback, setLibraryFeedback] = useState(null);

  const user = firebaseUser && !firebaseUser.isAnonymous ? firebaseUser : null;

  useEffect(() => {
    if (!libraryFeedback) return undefined;
    const timer = window.setTimeout(() => setLibraryFeedback(null), 2800);
    return () => window.clearTimeout(timer);
  }, [libraryFeedback]);

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    getFirebaseClient()
      .then(({ auth }) => {
        if (cancelled) return;
        unsubscribe = onAuthStateChanged(auth, (nextUser) => {
          setFirebaseUser(nextUser);
          setAuthReady(true);
        });
      })
      .catch(() => setAuthReady(true));

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setSpace({ profile: {}, preferences: {}, library: {}, experience: {} });
      return undefined;
    }

    let stop = () => {};
    let cancelled = false;
    getFirebaseClient().then(({ database }) => {
      if (cancelled) return;
      const spaceRef = ref(database, `userSpaces/${user.uid}`);
      stop = onValue(
        spaceRef,
        (snapshot) => {
          const value = snapshot.val() || {};
          setSpace({
            profile: value.profile || {},
            preferences: value.preferences || {},
            library: value.library || {},
            experience: value.experience || {},
          });
        },
        (error) => {
          console.error("Unable to sync personal space", error);
          setLibraryFeedback({ type: "error", message: "Your library could not be synced. Please refresh and try again." });
        }
      );
      update(ref(database, `userSpaces/${user.uid}/profile`), {
        displayName: user.displayName || "Hedone viewer",
        email: user.email || "",
        photoURL: user.photoURL || "",
        lastSeenAt: serverTimestamp(),
      }).catch(() => {});
      update(ref(database, `publicProfiles/${user.uid}`), {
        uid: user.uid,
        displayName: user.displayName || "Hedone viewer",
        photoURL: user.photoURL || "",
        handle: publicHandle(user),
        lastSeenAt: serverTimestamp(),
      }).catch(() => {});
    });

    return () => {
      cancelled = true;
      stop();
    };
  }, [user]);

  const openAuth = useCallback(() => {
    setAuthError("");
    setAuthOpen(true);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setAuthBusy(true);
    setAuthError("");
    try {
      const { auth, database } = await getFirebaseClient();
      let credential;

      if (isNativeApp()) {
        const nativeResult = await NativeFirebaseAuthentication.signInWithGoogle({
          skipNativeAuth: true,
        });
        const idToken = nativeResult?.credential?.idToken;
        const accessToken = nativeResult?.credential?.accessToken || null;
        if (!idToken) {
          throw new Error("Google did not return an ID token for Firebase authentication.");
        }
        const googleCredential = GoogleAuthProvider.credential(idToken, accessToken);
        credential = await signInWithCredential(auth, googleCredential);
      } else {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        credential = await signInWithPopup(auth, provider);
      }

      await update(ref(database, `userSpaces/${credential.user.uid}/profile`), {
        displayName: credential.user.displayName || "Hedone viewer",
        email: credential.user.email || "",
        photoURL: credential.user.photoURL || "",
        createdAt: serverTimestamp(),
        lastSeenAt: serverTimestamp(),
      });
      setAuthOpen(false);
    } catch (error) {
      if (!isNativeApp() && (
        error?.code === "auth/popup-blocked" ||
        error?.code === "auth/web-storage-unsupported"
      )) {
        const { auth } = await getFirebaseClient();
        const provider = new GoogleAuthProvider();
        await signInWithRedirect(auth, provider);
        return;
      }
      setAuthError(readableAuthError(error));
    } finally {
      setAuthBusy(false);
    }
  }, []);

  const logout = useCallback(async () => {
    const { auth } = await getFirebaseClient();
    if (isNativeApp()) {
      await NativeFirebaseAuthentication.signOut().catch(() => {});
    }
    await signOut(auth);
  }, []);

  const setLibraryFlag = useCallback(
    async (item, mediaType, flag) => {
      if (!user) {
        openAuth();
        return false;
      }

      const key = `${mediaType}_${item.id}`;
      const current = space.library[key] || {};
      const nextValue = !current[flag];
      const next = {
        ...cleanLibraryItem(item, mediaType),
        watchlist: Boolean(current.watchlist),
        liked: Boolean(current.liked),
        [flag]: nextValue,
        updatedAt: Date.now(),
      };
      const shouldRemove = !next.watchlist && !next.liked;

      setSpace((previous) => {
        const library = { ...previous.library };
        if (shouldRemove) delete library[key];
        else library[key] = next;
        return { ...previous, library };
      });

      try {
        const { database } = await getFirebaseClient();
        const itemRef = ref(database, `userSpaces/${user.uid}/library/${key}`);
        if (shouldRemove) await remove(itemRef);
        else await set(itemRef, { ...next, updatedAt: serverTimestamp() });
        const label = flag === "watchlist" ? "watch later" : "liked titles";
        setLibraryFeedback({
          type: "success",
          message: nextValue ? `Added to ${label}.` : `Removed from ${label}.`,
        });
        return nextValue;
      } catch (error) {
        setSpace((previous) => {
          const library = { ...previous.library };
          if (Object.keys(current).length) library[key] = current;
          else delete library[key];
          return { ...previous, library };
        });
        console.error("Unable to update library", error);
        setLibraryFeedback({ type: "error", message: "Could not save that change. Please try again." });
        return null;
      }
    },
    [openAuth, space.library, user]
  );

  const savePreferences = useCallback(
    async (preferences) => {
      if (!user) {
        openAuth();
        return false;
      }
      const { database } = await getFirebaseClient();
      await update(ref(database, `userSpaces/${user.uid}/preferences`), {
        ...preferences,
        updatedAt: serverTimestamp(),
      });
      return true;
    },
    [openAuth, user]
  );

  const saveExperienceItem = useCallback(
    async (section, key, value) => {
      if (!user) {
        openAuth();
        return false;
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(section) || !/^[a-zA-Z0-9_-]+$/.test(key)) {
        throw new Error("Invalid personal-space path");
      }
      const { database } = await getFirebaseClient();
      const itemRef = ref(database, `userSpaces/${user.uid}/experience/${section}/${key}`);
      if (value === null) await remove(itemRef);
      else await set(itemRef, { ...value, updatedAt: serverTimestamp() });
      return true;
    },
    [openAuth, user]
  );

  const value = useMemo(
    () => ({
      user,
      authReady,
      profile: space.profile,
      preferences: space.preferences,
      library: space.library,
      experience: space.experience,
      libraryItems: Object.values(space.library),
      openAuth,
      logout,
      setLibraryFlag,
      savePreferences,
      saveExperienceItem,
      isInLibrary: (mediaType, id, flag) =>
        Boolean(space.library[`${mediaType}_${id}`]?.[flag]),
    }),
    [authReady, logout, openAuth, saveExperienceItem, savePreferences, setLibraryFlag, space, user]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {libraryFeedback && (
        <div className={`library-toast is-${libraryFeedback.type}`} role="status" aria-live="polite">
          {libraryFeedback.message}
        </div>
      )}
      {authOpen && (
        <div className="auth-modal-backdrop" role="presentation" onMouseDown={() => setAuthOpen(false)}>
          <section className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="auth-title" onMouseDown={(event) => event.stopPropagation()}>
            <button className="auth-modal-close" onClick={() => setAuthOpen(false)} aria-label="Close sign-in"><FiX /></button>
            <span className="auth-brand-mark">H</span>
            <span className="auth-eyebrow">YOUR HEDONE</span>
            <h2 id="auth-title">Make every watch feel personal.</h2>
            <p>Save titles across devices, shape your taste profile, and get recommendations that improve as you use Hedone.</p>
            <div className="auth-benefits">
              <span>One private watchlist</span><span>Smarter recommendations</span><span>Likes synced everywhere</span>
            </div>
            <button className="google-signin" onClick={loginWithGoogle} disabled={authBusy}>
              <b>G</b>{authBusy ? "Connecting…" : "Continue with Google"}
            </button>
            {authError && <p className="auth-error" role="alert">{authError}</p>}
            <small>No password to remember. Your library is private to your Google account.</small>
          </section>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
};
